import {
  BuildingOffice2Icon,
  FolderIcon,
  UsersIcon,
  TagIcon,
  CalendarDaysIcon,
  EnvelopeIcon,
  EyeSlashIcon,
  ShoppingBagIcon,
  EnvelopeOpenIcon,
  ChartBarIcon,
  BookOpenIcon,
  DocumentDuplicateIcon,
  RectangleStackIcon,
  MegaphoneIcon,
  CurrencyDollarIcon,
  ClipboardDocumentListIcon,
  MapPinIcon,
  BellIcon,
  ShieldCheckIcon,
  AcademicCapIcon,
  CalculatorIcon,
  ShoppingCartIcon,
  TrophyIcon,
} from "@heroicons/react/24/outline";
import { NavigationItem } from "./types";
import {
  BarChart3,
  Clock,
  FileSpreadsheet,
  GraduationCap,
  UserRoundPlus,
  TicketIcon,
  Percent,
  Stethoscope,
} from "lucide-react";

/**
 * Each NavigationItem can have:
 * - name (string)
 * - href (string)
 * - icon (React component)
 * - current (boolean)
 * - internalName (string)
 * - permissions (string[]) - nav-item based permissions
 * - isCertainPathOnly (boolean)
 * - children?: NavigationItem[]
 */
const navigation: NavigationItem[] = [
  {
    name: "Events",
    href: "/admin/events",
    icon: CalendarDaysIcon,
    current: false,
    internalName: "event-registration-admin",
    permissions: ["CAN_MANAGE_EVENTS"],
    isCertainPathOnly: true,
    children: [
      {
        name: "Management",
        href: "/admin/events",
        icon: ClipboardDocumentListIcon,
        current: false,
        isCertainPathOnly: true,
        permissions: ["CAN_MANAGE_EVENTS"],
        internalName: "event-management",
      },
      {
        name: "Categories",
        href: "/admin/event-categories",
        icon: FolderIcon,
        current: false,
        isCertainPathOnly: true,
        permissions: ["CAN_MANAGE_EVENTS"],
        internalName: "event_categories",
      },
      {
        name: "Instructors",
        href: "/admin/event-instructors",
        icon: GraduationCap,
        current: false,
        isCertainPathOnly: true,
        permissions: ["CAN_MANAGE_EVENTS"],
        internalName: "event_instructors",
      },
      {
        name: "Venues",
        href: "/admin/venues",
        icon: MapPinIcon,
        current: false,
        isCertainPathOnly: true,
        permissions: ["CAN_MANAGE_EVENTS"],
        internalName: "event_venues",
      },
      {
        name: "Vouchers",
        href: "/admin/vouchers",
        icon: TicketIcon,
        current: false,
        isCertainPathOnly: true,
        permissions: ["CAN_MANAGE_EVENTS"],
        internalName: "event_vouchers",
      },
      {
        name: "Discounts",
        href: "/admin/discounts",
        icon: Percent,
        current: false,
        isCertainPathOnly: true,
        permissions: ["CAN_MANAGE_EVENTS"],
        internalName: "event_discounts",
      },
      {
        name: "Notifications",
        href: "/admin/event-notifications",
        icon: BellIcon,
        current: false,
        isCertainPathOnly: true,
        permissions: ["CAN_MANAGE_EVENTS"],
        internalName: "event_notifications",
      },
    ],
  },

  {
    name: "Email Management",
    href: "/admin/email-templates",
    icon: EnvelopeIcon,
    current: false,
    internalName: "email_management",
    permissions: ["administration-email-management"],
    isCertainPathOnly: true,
    children: [
      {
        name: "Email Templates",
        href: "/admin/email-templates",
        icon: DocumentDuplicateIcon,
        current: false,
        internalName: "email_templates",
        isCertainPathOnly: true,
        permissions: ["administration-email-management"],
      },
      {
        name: "Email Campaigns",
        href: "/admin/email-campaigns",
        icon: MegaphoneIcon,
        current: false,
        internalName: "email_campaigns",
        isCertainPathOnly: true,
        permissions: ["administration-email-management"],
      },
      {
        name: "Template Categories",
        href: "/admin/email-template-categories",
        icon: FolderIcon,
        current: false,
        internalName: "email_template_categories",
        isCertainPathOnly: true,
        permissions: ["administration-email-management"],
      },
      {
        name: "Email Event Logs",
        href: "/admin/email/activity",
        icon: ChartBarIcon,
        current: false,
        internalName: "email_event_logs",
        isCertainPathOnly: true,
        permissions: ["administration-email-management"],
      },
    ],
  },

  {
    name: "Resource Library",
    href: "#",
    icon: BookOpenIcon,
    current: false,
    internalName: "resource_management",
    permissions: ["administration-resource-library"],
    isCertainPathOnly: true,
    children: [
      {
        name: "Resource Management",
        href: "/admin/resources",
        icon: RectangleStackIcon,
        current: false,
        internalName: "resource_management",
        permissions: ["administration-resource-library"],
        isCertainPathOnly: true,
      },
      {
        name: "File Manager",
        href: "#file-manager",
        icon: FolderIcon,
        current: false,
        internalName: "file_manager",
        permissions: ["administration-resource-library"],
        isCertainPathOnly: true,
        isDialog: true,
        dialogAction: "fileManager",
      },
      {
        name: "Resource Categories",
        href: "/admin/resource-categories",
        icon: FolderIcon,
        current: false,
        internalName: "resourceCategories",
        permissions: ["administration-resource-library"],
        isCertainPathOnly: true,
      },
      {
        name: "Resource Tags",
        href: "/admin/resource-tags",
        icon: TagIcon,
        current: false,
        internalName: "resourceTags",
        permissions: ["administration-resource-library"],
        isCertainPathOnly: true,
      },
    ],
  },

  {
    name: "CapEx",
    href: "#",
    icon: CalculatorIcon,
    current: false,
    internalName: "capex",
    roles: ["ROLE_SUPER_ADMIN"],
    isCertainPathOnly: true,
    children: [
      {
        name: "CapEx Report",
        href: "/capex",
        icon: FileSpreadsheet,
        current: false,
        internalName: "capex_report",
        roles: ["ROLE_SUPER_ADMIN"],
        isCertainPathOnly: true,
      },
      {
        name: "Monthly Summary",
        href: "/capex/summary",
        icon: BarChart3,
        current: false,
        internalName: "capex_summary",
        roles: ["ROLE_SUPER_ADMIN"],
        isCertainPathOnly: true,
      },
      {
        name: "Bulk Hours",
        href: "/capex/bulk-hours",
        icon: Clock,
        current: false,
        internalName: "capex_bulk_hours",
        roles: ["ROLE_SUPER_ADMIN"],
        isCertainPathOnly: true,
      },
    ],
  },

  {
    name: "Scoreboard",
    href: "#",
    icon: TrophyIcon,
    current: false,
    internalName: "scoreboard",
    roles: ["ROLE_LEARNING_ADMIN", "ROLE_SUPER_ADMIN"],
    isCertainPathOnly: true,
    children: [
      {
        name: "Monthly Report",
        href: "/admin/scoreboard/monthly-report",
        icon: ChartBarIcon,
        current: false,
        internalName: "scoreboard_monthly_report",
        roles: ["ROLE_LEARNING_ADMIN", "ROLE_SUPER_ADMIN"],
        isCertainPathOnly: true,
      },
      {
        name: "Leaderboards",
        href: "/admin/scoreboard/leaderboards",
        icon: TrophyIcon,
        current: false,
        internalName: "scoreboard_leaderboards",
        roles: ["ROLE_LEARNING_ADMIN", "ROLE_SUPER_ADMIN"],
        isCertainPathOnly: true,
      },
    ],
  },

  {
    name: "Company & Users",
    href: "#",
    icon: BuildingOffice2Icon,
    current: false,
    internalName: "user_management",
    permissions: ["administration-companies", "administration-users"],
    isCertainPathOnly: true,
    children: [
      {
        name: "Company Management",
        href: "/admin/companies",
        icon: BuildingOffice2Icon,
        current: false,
        internalName: "company_management",
        permissions: ["administration-companies"],
        isCertainPathOnly: true,
      },
      {
        name: "User Management",
        href: "/admin/users",
        icon: UsersIcon,
        current: false,
        internalName: "admin_user_list",
        permissions: ["administration-users"],
        isCertainPathOnly: true,
      },
      {
        name: "Business Roles",
        href: "/admin/business-roles",
        icon: ShieldCheckIcon,
        current: false,
        internalName: "business_roles",
        permissions: ["administration-users"],
        isCertainPathOnly: true,
      },
      {
        name: "Coach Assignments",
        href: "/admin/coach-assignments",
        icon: UserRoundPlus,
        current: false,
        internalName: "coach_assignments",
        roles: ["ROLE_SUPER_ADMIN"],
        isCertainPathOnly: true,
      },
    ],
  },

  {
    name: "Product Store",
    href: "#",
    icon: ShoppingCartIcon,
    current: false,
    internalName: "product_storefront_admin",
    roles: ["ROLE_SUPER_ADMIN"],
    permissions: [],
    isCertainPathOnly: true,
    children: [
      {
        name: "Product Management",
        href: "/product-storefront/admin/products",
        icon: ShoppingBagIcon,
        current: false,
        isCertainPathOnly: true,
        roles: ["ROLE_SUPER_ADMIN"],
        internalName: "product-management",
        permissions: [],
      },
      {
        name: "Categories",
        href: "/product-storefront/admin/product-categories",
        icon: FolderIcon,
        current: false,
        isCertainPathOnly: true,
        roles: ["ROLE_SUPER_ADMIN"],
        internalName: "product_categories",
        permissions: [],
      },
      {
        name: "Orders",
        href: "/product-storefront/admin/orders",
        icon: ClipboardDocumentListIcon,
        current: false,
        isCertainPathOnly: true,
        roles: ["ROLE_SUPER_ADMIN"],
        internalName: "product_orders",
        permissions: [],
      },
      {
        name: "Reports",
        href: "/product-storefront/admin/reports",
        icon: ChartBarIcon,
        current: false,
        isCertainPathOnly: true,
        roles: ["ROLE_SUPER_ADMIN"],
        internalName: "product_reports",
        permissions: [],
      },
    ],
  },

  {
    name: "Reports",
    href: "/admin/reports",
    icon: ClipboardDocumentListIcon,
    current: false,
    internalName: "reports",
    roles: ["ROLE_SUPER_ADMIN"],
    isCertainPathOnly: true,
  },

  {
    name: "Games",
    href: "#",
    icon: AcademicCapIcon,
    current: false,
    internalName: "games",
    roles: ["ROLE_SUPER_ADMIN", "ROLE_LEARNING_ADMIN"],
    isCertainPathOnly: true,
    children: [
      {
        name: "Scenario Admin",
        href: "/games/admin/scenarios",
        icon: AcademicCapIcon,
        current: false,
        internalName: "scenario_admin",
        roles: ["ROLE_SUPER_ADMIN", "ROLE_LEARNING_ADMIN"],
        isCertainPathOnly: true,
      },
      {
        name: "Play Game",
        href: "/games/door-to-done",
        icon: AcademicCapIcon,
        current: false,
        internalName: "play_game",
        roles: ["ROLE_SUPER_ADMIN", "ROLE_LEARNING_ADMIN"],
        isCertainPathOnly: true,
      },
    ],
  },

  {
    name: "Lead Generation",
    href: "#",
    icon: ChartBarIcon,
    current: false,
    internalName: "admin",
    permissions: ["administration-stochastic"],
    isCertainPathOnly: true,
    children: [
      {
        name: "Campaign Status",
        href: "/lead-generation/campaign-status",
        icon: ChartBarIcon,
        current: false,
        internalName: "stochastic_campaign_status",
        permissions: ["administration-stochastic"],
      },
      {
        name: "Campaign Job Status",
        href: "/lead-generation/campaign-job-status",
        icon: MegaphoneIcon,
        current: false,
        internalName: "stochastic_mailing",
        permissions: ["administration-stochastic"],
      },
      {
        name: "Campaign Billing",
        href: "/lead-generation/campaigns/billing",
        icon: CurrencyDollarIcon,
        current: false,
        internalName: "campaign_billing",
        permissions: ["administration-stochastic"],
      },
      {
        name: "Postage Expense",
        href: "/lead-generation/campaigns/postage",
        icon: EnvelopeOpenIcon,
        current: false,
        internalName: "campaignPostage",
        permissions: ["administration-stochastic"],
      },
      {
        name: "Do Not Mail",
        href: "/lead-generation/do-not-mail",
        icon: EyeSlashIcon,
        current: false,
        internalName: "doNotMail",
        permissions: ["administration-stochastic"],
      },
      {
        name: "Manage Products",
        href: "/lead-generation/products/campaign",
        icon: ShoppingBagIcon,
        current: false,
        internalName: "campaignProducts",
        permissions: ["administration-stochastic"],
      },
      {
        name: "Draft Invoice Calculator",
        href: "/lead-generation/draft-invoice",
        icon: CalculatorIcon,
        current: false,
        internalName: "draft_invoice_calculator",
        permissions: ["administration-stochastic"],
      },
    ],
  },
  {
    // A diagnostic assesses a company, so this lives with the tenant-level
    // administration tools rather than anywhere member-facing. Flat rather
    // than a submenu: the authoring library was retired, leaving one screen.
    name: "Diagnostics",
    href: "/admin/diagnostics",
    icon: Stethoscope,
    current: false,
    internalName: "diagnostics",
    permissions: ["CAN_ADMINISTER_DIAGNOSTICS"],
    // DiagnosticVoter wants the CertainPath company as well as the permission,
    // so a coach employed at a member company cannot read every tenant's
    // placements. Offering them the link would only lead to a 403.
    isCertainPathOnly: true,
  },
];

const adminConfig = {
  sectionName: "admin",
  defaultRoute: "/admin/companies",
  navigation,
};

export default adminConfig;
